/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Image } from '@coursera-web/cds-labs';

import type { ButtonProps, IconButtonProps } from '@coursera/cds-core';
import { Button, Hidden, IconButton, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import CourseraPlusLogo from 'images/coursera-plus/coursera-plus-logo-whitetext-blueplus.png';
import BgImage from 'images/coursera-plus/promo-bar-bgimage.png';

import _t from 'i18n!nls/coursera-plus';

const TrackedCdsButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);
const TrackedCdsIconButton = withSingleTracked({ type: 'BUTTON' })<IconButtonProps>(IconButton);

const BGIMAGE_Z_INDEX = 1;

const styles = {
  container: css`
    width: 100%;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-blue-700);
    color: var(--cds-color-neutral-primary-invert);
    padding: var(--cds-spacing-200);
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: var(--cds-spacing-200);
    overflow: hidden;

    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr auto;
    }
  `,
  flexCenter: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
  `,
  buttonContainer: css`
    flex-direction: row;
    gap: var(--cds-spacing-100);
    position: relative;

    img {
      z-index: ${BGIMAGE_Z_INDEX};
      position: absolute;
      max-width: unset !important;
      height: 200px !important;
      right: -75px;

      ${breakpoints.down('sm')} {
        right: -150px;
      }

      ${breakpoints.down('xs')} {
        display: none;
      }
    }

    a,
    button {
      z-index: ${BGIMAGE_Z_INDEX + 1};

      /* this gets rid of the blue outline around the CTA so it matches the close button */
      box-shadow: none;
    }

    ${breakpoints.down('xs')} {
      display: block;
    }
  `,
};

type BaseCtaButton = {
  text: string;
  trackingName?: string;
};

type LinkButton = {
  href: string;
} & BaseCtaButton;

type ActionButton = {
  onClick: () => void;
} & BaseCtaButton;

type Props = {
  title: string;
  subtitle: string | React.ReactElement;
  button?: LinkButton | ActionButton;
  dismissable?: boolean;
  onDismiss?: () => void;
  isDismissed?: boolean;
  displayBgImage?: boolean;
  ctaClickTrackingCallback?: () => void;
};

const PromoNotificationBar = React.forwardRef<HTMLDivElement, Props>(
  (
    { title, subtitle, button, dismissable, onDismiss, isDismissed, displayBgImage, ctaClickTrackingCallback }: Props,
    ref
  ) => {
    const track = useTracker();
    const isMdDown = useMediaQuery(breakpoints.down('md'));

    if (isDismissed) {
      return null;
    }

    let cta: React.ReactElement | undefined;

    if (button && 'href' in button) {
      cta = (
        <Button
          component={TrackedLink2}
          href={button.href}
          trackingName={button.trackingName ?? 'cplus_promo_bar_cta'}
          onClick={ctaClickTrackingCallback}
          role="link"
          variant="primaryInvert"
          size="small"
        >
          {button.text}
        </Button>
      );
    } else if (button && 'onClick' in button) {
      cta = (
        <TrackedCdsButton
          trackingName={button.trackingName ?? 'cplus_promo_bar_cta'}
          onClick={() => {
            button.onClick();
            ctaClickTrackingCallback?.();
          }}
          variant="primaryInvert"
          size="small"
        >
          {button.text}
        </TrackedCdsButton>
      );
    }

    const cplusLogoImage = (height: number) => (
      <Image description={_t('Coursera Plus Logo')} url={CourseraPlusLogo} height={height} />
    );

    const content = (
      <div css={styles.container} ref={ref}>
        <Hidden smDown>
          <div css={styles.flexCenter}>{cplusLogoImage(isMdDown ? 14 : 17.5)}</div>
        </Hidden>

        <div
          css={css`
            ${breakpoints.down('sm')} {
              display: flex;
              flex-direction: column;
              gap: var(--cds-spacing-100);
            }
          `}
        >
          <Hidden mdUp>{cplusLogoImage(10.5)}</Hidden>
          <Typography2 component="h2" variant="subtitleLarge" color="inherit">
            {title}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="inherit">
            {subtitle}
          </Typography2>
          {button && <Hidden smUp>{cta}</Hidden>}
        </div>

        <div css={[styles.flexCenter, styles.buttonContainer]}>
          {displayBgImage && <Image url={BgImage} />}
          {button && <Hidden only="xs">{cta}</Hidden>}
          {dismissable && (
            <TrackedCdsIconButton
              trackingName="dismiss_cplus_promo_bar"
              variant="primaryInvert"
              intent="close"
              size="small"
              onClick={() => {
                onDismiss?.();
                track('click_button', {
                  button: {
                    name: 'dismiss',
                  },
                });
              }}
            />
          )}
        </div>
      </div>
    );

    return content;
  }
);

export default PromoNotificationBar;
